/**
 * Deep-link helpers for feed events. Maps an event's `reference_type` /
 * `reference_id` (and recommendation `sample_ids`) to in-app routes so a feed
 * row can jump straight to the booking or rate card it describes.
 *
 * Only reference kinds that have a real route in frontend-v2 return a link;
 * everything else degrades to a plain, non-clickable row.
 *
 * List hrefs inherit the caller's linked-bookings scope (sales person lock, or
 * Opened by when "only my bookings" is on) so insight "view all" matches the
 * slice the insight was computed on.
 */
import { buildBookingsFilterUrl } from "@/lib/bookingsFilterLink";
import { isoWeekToDateRange, toWeekLabel } from "@/lib/weekDates";
import { splitLaneKey } from "@/lib/formatPort";
import {
  applyViewerScopeToBookings,
  liveViewerScope,
} from "@/lib/viewerScopeFilters";
import type { BookingsFilters } from "@/types/api";
import { expandPendingStateParam } from "@/types/booking";
import type {
  CommercialDigestPayload,
  CommercialDigestShowMore,
  FeedEvent,
  OperationalDigestPayload,
  OperationalDigestShowMore,
} from "@/types/feed";
import type { FeedRowCornerLink } from "./FeedRowCornerActions";

function scopedBookingsHref(filters: BookingsFilters): string {
  return buildBookingsFilterUrl(
    applyViewerScopeToBookings(filters, liveViewerScope()),
  );
}

/** Route for a booking source id. */
export function bookingHref(sourceId: string): string {
  return `/bookings/${encodeURIComponent(sourceId)}`;
}

/** Route for a rate card id. */
export function rateHref(cardId: string): string {
  return `/rates/${encodeURIComponent(cardId)}`;
}

/**
 * Route for a rate card, optionally highlighting one rate line
 * (`/rates/{card}?line={line}`). `RateLinesPanel` opens + highlights the line.
 */
export function rateLineHref(
  cardId: string,
  lineId?: string | null,
): string {
  const base = rateHref(cardId);
  return lineId ? `${base}?line=${encodeURIComponent(lineId)}` : base;
}

/** Bookings list filtered to one customer (consignee). */
export function customerBookingsHref(consignee: string): string {
  return scopedBookingsHref({ consignee });
}

/** Bookings list filtered to one sales person. */
export function salesPersonBookingsHref(salesPerson: string): string {
  return scopedBookingsHref({ sales_person: salesPerson });
}

/** Bookings list filtered to one operator (Focus "Open By"). */
export function operatorBookingsHref(openedBy: string): string {
  return scopedBookingsHref({ opened_by: openedBy });
}

/** Bookings list filtered to one handling agent. */
export function agentBookingsHref(agent: string): string {
  return scopedBookingsHref({ agent });
}

/** Bookings list filtered to one carrier. */
export function carrierBookingsHref(carrier: string): string {
  return scopedBookingsHref({ carrier });
}

/** Bookings list filtered to one booking state. */
export function stateBookingsHref(state: string): string {
  return scopedBookingsHref({ state: expandPendingStateParam(state) ?? state });
}

/** Bookings opened during the ISO week that contains `weekStart` (`YYYY-MM-DD`). */
export function openedInWeekHref(weekStart: string): string | null {
  const label = toWeekLabel(weekStart);
  if (!label) return null;
  const range = isoWeekToDateRange(label);
  if (!range) return null;
  return scopedBookingsHref({
    open_date_from: range.from,
    open_date_to: range.to,
  });
}

/** Origin + destination filters for a locode lane key (`CNNGB→ILASH`). */
export function laneBookingsFilters(
  laneKey: string | null | undefined,
): BookingsFilters | null {
  const parts = splitLaneKey(laneKey);
  if (!parts) return null;
  return {
    origin_port: parts.origin,
    destination_port: parts.destination,
  };
}

/** Bookings list filtered to one lane (origin + destination locodes). */
export function laneBookingsHref(laneKey: string): string | null {
  const filters = laneBookingsFilters(laneKey);
  return filters ? scopedBookingsHref(filters) : null;
}

/** Bookings list filtered to rebook opportunities (portfolio funnel drill-in). */
export function opportunityBookingsHref(): string {
  return scopedBookingsHref({ has_opportunity: "yes" });
}

/** Bookings list with only the viewer's scope applied. */
export function allBookingsHref(): string {
  return scopedBookingsHref({});
}

/** The best in-app deep link for an event, or null when none applies. */
export function feedEventHref(
  event: Pick<FeedEvent, "reference_type" | "reference_id"> &
    Partial<Pick<FeedEvent, "payload">>,
): string | null {
  const { reference_type, reference_id } = event;
  if (reference_id) {
    switch (reference_type) {
      case "booking":
        return bookingHref(reference_id);
      case "rate_card":
      case "rate":
        return rateHref(reference_id);
      default:
        break;
    }
  }
  // Legacy safety: per-booking opportunity roots deep-link from their payload
  // `source_id` even if the reference fields are missing.
  if (
    event.payload &&
    (event.payload as { kind?: string }).kind === "booking_opportunity"
  ) {
    const sourceId =
      readPayloadString(event.payload, "source_id") ??
      readPayloadString(
        (event.payload as { booking?: unknown }).booking,
        "source_id",
      );
    if (sourceId) return bookingHref(sourceId);
  }
  return null;
}

/**
 * Primary list / detail links for a feed row header. Shown in the top-right
 * corner so they remain visible when the card is collapsed.
 */
export function feedRowCornerLinks(
  event: Pick<FeedEvent, "reference_type" | "reference_id"> &
    Partial<Pick<FeedEvent, "payload" | "entity_scope">>,
  digests?: {
    operational?: OperationalDigestPayload | null;
    commercial?: CommercialDigestPayload | null;
  },
): FeedRowCornerLink[] {
  const links: FeedRowCornerLink[] = [];
  const href = feedEventHref(event);
  if (href) {
    links.push({ href, label: feedEventLinkLabel(event) });
  } else {
    const batchHref = batchScopeHref(event.payload);
    if (batchHref) {
      links.push({ href: batchHref, label: "View bookings" });
    }
  }

  if (
    event.payload &&
    (event.payload as { kind?: string }).kind === "carrier_delay"
  ) {
    const delayHref = carrierDelayBookingsHref(event);
    if (delayHref && !links.some((l) => l.href === delayHref)) {
      links.push({ href: delayHref, label: "View bookings" });
    }
  }

  const operational = digests?.operational;
  if (operational) {
    const showMoreHref = operationalDigestShowMoreHref(
      operational.show_more,
      operational.scope,
      event.entity_scope?.booking_ids,
    );
    const total = operational.booking_count;
    const shown = operational.bookings.length;
    links.push({
      href: showMoreHref,
      label:
        total > shown
          ? `Show all ${total} bookings`
          : "View bookings",
    });
  }

  const commercial = digests?.commercial;
  if (commercial) {
    const showMoreHref = commercialDigestShowMoreHref(
      commercial.show_more,
      commercial.scope,
    );
    const total = commercial.booking_count;
    links.push({
      href: showMoreHref,
      label: `Show all ${total} booking${total === 1 ? "" : "s"}`,
    });
  }

  return links;
}

/** Human label for the deep-link affordance, matching {@link feedEventHref}. */
export function feedEventLinkLabel(
  event: Pick<FeedEvent, "reference_type">,
): string {
  switch (event.reference_type) {
    case "booking":
      return "View booking";
    case "rate_card":
    case "rate":
      return "View rate card";
    default:
      return "View";
  }
}

function readPayloadString(payload: unknown, field: string): string | null {
  if (payload && typeof payload === "object") {
    const v = (payload as Record<string, unknown>)[field];
    if (typeof v === "string" && v.trim()) return v;
  }
  return null;
}

/** Bookings list filtered to one portfolio slice (sales person / opener / agent). */
export function portfolioBookingsHref(
  dimension: string,
  key: string,
): string | null {
  switch (dimension) {
    case "sales_person":
      return salesPersonBookingsHref(key);
    case "opened_by":
      return operatorBookingsHref(key);
    case "agent":
      return agentBookingsHref(key);
    default:
      return null;
  }
}

/**
 * "Show more" bookings link for an operational digest card. The digest payload's
 * `show_more` descriptor already carries the exact bookings filter query params
 * (date bounds + optional `group_by`, matching {@link buildBookingsFilterUrl}
 * keys), so it is passed straight through — then scoped to the viewer so the
 * table matches the portfolio the card was emitted for.
 */
/** Max digest bookings linked by explicit `source_id` list (URL size). */
const MAX_DIGEST_SOURCE_ID_LINK = 200;

export function operationalDigestShowMoreHref(
  showMore: OperationalDigestShowMore | null | undefined,
  scope?: { dimension?: string; key?: string } | null,
  bookingIds?: string[] | null,
): string {
  if (
    bookingIds &&
    bookingIds.length > 0 &&
    bookingIds.length <= MAX_DIGEST_SOURCE_ID_LINK
  ) {
    return scopedBookingsHref({ source_id: bookingIds.join(",") });
  }

  const filters: BookingsFilters = {};
  if (showMore) {
    for (const [key, value] of Object.entries(showMore)) {
      if (key === "group_by") continue;
      if (typeof value === "string" && value.trim()) {
        (filters as Record<string, string>)[key] = value;
      }
    }
  }
  if (scope?.key) {
    switch (scope.dimension) {
      case "sales_person":
        filters.sales_person = scope.key;
        break;
      case "opened_by":
        filters.opened_by = scope.key;
        break;
      case "agent":
        filters.agent = scope.key;
        break;
    }
  }
  return scopedBookingsHref(filters);
}

/**
 * "Show more" bookings link for a commercial digest card. The payload's
 * `show_more` descriptor carries the opportunity-kind filter (`rebook` /
 * `first_book`) and `has_opportunity=yes`; the card's owning portfolio slice
 * (`scope.dimension` / `scope.key`) is added so an admin drilling in matches the
 * owner the card was emitted for, then the result is scoped to the viewer.
 */
export function commercialDigestShowMoreHref(
  showMore: CommercialDigestShowMore | null | undefined,
  scope?: { dimension?: string; key?: string } | null,
): string {
  const filters: BookingsFilters = {};
  if (showMore) {
    for (const [key, value] of Object.entries(showMore)) {
      if (typeof value === "string" && value.trim()) {
        (filters as Record<string, string>)[key] = value;
      }
    }
  }
  if (scope?.key) {
    switch (scope.dimension) {
      case "sales_person":
        filters.sales_person = scope.key;
        break;
      case "opened_by":
        filters.opened_by = scope.key;
        break;
      case "agent":
        filters.agent = scope.key;
        break;
    }
  }
  return scopedBookingsHref(filters);
}

/** Bookings drill-down for a scoped `BatchIngested` root, by its portfolio. */
export function batchScopeHref(payload: unknown): string | null {
  const scope = readPayloadString(payload, "scope");
  const key = readPayloadString(payload, "dimension_key");
  if (!scope || scope === "org" || !key) return null;
  return portfolioBookingsHref(scope, key);
}

/** Bookings drill-down for an aggregated data-quality alert (per agent). */
export function alertBookingsHref(payload: unknown): string | null {
  const agent = readPayloadString(payload, "agent");
  return agent ? agentBookingsHref(agent) : null;
}

/**
 * "View bookings" drill-down for a grouped carrier-delay / tracking-drop card.
 * A single affected booking links straight to that booking; several fall back to
 * the carrier-scoped bookings list so the reader lands on the impacted set.
 */
export function carrierDelayBookingsHref(
  event: Partial<Pick<FeedEvent, "payload" | "entity_scope">>,
): string | null {
  const ids = event.entity_scope?.booking_ids ?? [];
  if (ids.length === 1) return bookingHref(ids[0]);
  const carrier = readPayloadString(event.payload, "carrier");
  if (carrier) return scopedBookingsHref({ carrier });
  if (ids.length > 0) return bookingHref(ids[0]);
  return null;
}

/**
 * Booking detail link for a per-booking first-book child effect
 * (`kind: "booking_first_book"`). Returns null for any other payload so the
 * generic reference-based link handling applies.
 */
export function bookingFirstBookHref(payload: unknown): string | null {
  if (readPayloadString(payload, "kind") !== "booking_first_book") return null;
  const sourceId = readPayloadString(payload, "source_id");
  return sourceId ? bookingHref(sourceId) : null;
}

/** Extract recommendation sample booking ids from an effect payload, if any. */
export function recommendationSampleIds(payload: unknown): string[] {
  if (
    payload &&
    typeof payload === "object" &&
    (payload as Record<string, unknown>).kind === "recommendations"
  ) {
    const ids = (payload as Record<string, unknown>).sample_ids;
    if (Array.isArray(ids)) {
      return ids.filter((v): v is string => typeof v === "string");
    }
  }
  return [];
}
